import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { USER_ROLES, type UserRole } from '@kabbi/shared/constants';

// Auth endpoints can't go through the axios `api` instance because that
// instance imports useAuthStore (for the bearer-token interceptor), so
// using it here would create a circular import. Mirror the base-URL logic
// from lib/api.ts: split-domain prod sets VITE_API_URL=https://api.kabbi.io/api,
// dev/single-domain falls back to '/api' (same-origin).
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  // Multi-role set. Always includes USER_ROLES.PASSENGER (server enforces).
  roles: UserRole[];
  hasPassword?: boolean;
}

/** True when the user holds the given role. */
export function userHasRole(user: AuthUser | null, role: UserRole): boolean {
  return !!user?.roles.includes(role);
}

/** True when the user holds at least one of the given roles. */
export function userHasAnyRole(user: AuthUser | null, roles: UserRole[]): boolean {
  return roles.some((r) => user?.roles.includes(r));
}

// Re-export for downstream consumers that already had USER_ROLES imported via
// authStore.
export { USER_ROLES };
export type { UserRole };

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, force?: boolean) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateUser: (partial: Partial<AuthUser>) => void;
  setAuth: (user: AuthUser, accessToken: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      login: async (email: string, password: string, force = false) => {
        // Send the browser's currently-stored refresh token (if any) so the
        // server can recognise a re-login from THIS browser and drop its own
        // session family before the single-session check — otherwise a
        // leftover/rotated session would falsely trip OTHER_SESSION_ACTIVE
        // against the user themselves.
        const stored = get().refreshToken;
        const response = await fetch(`${API_BASE}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, force, refreshToken: stored ?? undefined }),
        });
        if (!response.ok) {
          const data = await response.json();
          // Special-case 409 — the SPA prompts the user to confirm
          // killing other sessions, then retries with force=true.
          const err = new Error(data.error || 'Login failed') as Error & {
            code?: string;
            activeSessions?: number;
          };
          err.code = data.code;
          if (typeof data.activeSessions === 'number') err.activeSessions = data.activeSessions;
          throw err;
        }
        const { user, accessToken, refreshToken } = await response.json();
        set({ user, accessToken, refreshToken, isAuthenticated: true });
      },

      logout: () => {
        const { refreshToken } = get();
        if (refreshToken) {
          fetch(`${API_BASE}/auth/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          }).catch(() => {});
        }
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },

      refresh: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error('No refresh token');
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) throw new Error('Refresh failed');
        // Refresh rotates: the server issues a new refresh token alongside
        // the new access token. Persist both — the prior `refreshToken` is
        // now revoked server-side and re-using it would trip replay
        // detection and burn this session family back to login.
        const { accessToken, refreshToken: nextRefreshToken } = await response.json();
        set({ accessToken, refreshToken: nextRefreshToken });
      },

      checkAuth: async () => {
        const { accessToken, refresh, logout } = get();
        if (!accessToken) { logout(); return; }
        try {
          const res = await fetch(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (res.ok) {
            const user = await res.json();
            set({ user });
            return;
          }
          // Access token invalid — try refreshing
          await refresh();
          const res2 = await fetch(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${get().accessToken}` },
          });
          if (!res2.ok) logout();
          else set({ user: await res2.json() });
        } catch {
          logout();
        }
      },

      updateUser: (partial) => {
        const current = get().user;
        if (current) set({ user: { ...current, ...partial } });
      },

      setAuth: (user, accessToken, refreshToken) => {
        set({ user, accessToken, refreshToken, isAuthenticated: true });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
